import { formatDate } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { Settlement } from 'src/app/classes/settlement.class';
import { User } from 'src/app/classes/user.class';
import { NON_TIME_HOURTYPES, SettlementsService } from 'src/app/services/settlements.service';
import { Week } from '../../../classes/week';
import { WeekDay } from '../../../classes/weekday';
import { Settleable } from '../settleable.interface';

export const TRAVEL_HOURTYPE_CODES = ['RU', 'RUA', 'CRW'];
export const CORRECTION_PREFIX = 'Correctie: ';
const MAX_DESCRIPTION_LENGTH = 255;
const ROUNDING_MINUTES = 3;

export type ResettleDifference = {
    date: Date;
    hourtypeCode: string;
    hourtypeDescription: string;
    current: number;
    recalculated: number;
    delta: number;
    correctable: boolean;
    useRecalculated: boolean;
    afasProjectId: string;
    description: string;
    obsoleteCorrectionIds: number[];
};

type WeekDayFormValue = ReturnType<WeekDay['fg']['getRawValue']>;

type ResettleSnapshot = {
    weeks: { week: Week; sendToAfas: boolean; settling: boolean }[];
    days: {
        day: WeekDay;
        settlements: Settlement[];
        settling: boolean;
        travelTo: string;
        fg: WeekDayFormValue;
        fgSecond: WeekDayFormValue;
    }[];
};

/**
 * Draait de verloning opnieuw zonder iets op te slaan, vergelijkt het resultaat met wat er nu staat
 * en maakt na akkoord aanvullende correctieregels aan.
 *
 * Corrigeert alleen positieve verschillen; bestaande regels blijven ongewijzigd.
 */
@Injectable()
export class ResettleService {
    private readonly settlementService = inject(SettlementsService);

    async calculateDifferences(
        weeks: Week[],
        settleService: Settleable,
        recalculateTravel: boolean
    ): Promise<ResettleDifference[]> {
        const snapshot = this.takeSnapshot(weeks);

        this.settlementService.dryRun = true;
        settleService.dryRun = true;

        try {
            for (const week of weeks) {
                await settleService.settleWeekHours(week);
            }

            return this.compare(weeks, snapshot, recalculateTravel, settleService);
        } finally {
            this.settlementService.dryRun = false;
            settleService.dryRun = false;

            this.restoreSnapshot(snapshot);
        }
    }

    /**
     * Legt regels, formulierwaarden en vlaggen vast zoals ze vóór de proefverrekening waren, zodat
     * `restoreSnapshot` het scherm daarna onveranderd achterlaat.
     */
    private takeSnapshot(weeks: Week[]): ResettleSnapshot {
        return {
            weeks: weeks.map(week => ({ week, sendToAfas: week.sendToAfas, settling: week.settling })),
            days: weeks.flatMap(week =>
                week.weekDays.map(day => ({
                    day,
                    settlements: day.settlements,
                    settling: day.settling,
                    travelTo: day.travelTo,
                    fg: day.fg?.getRawValue(),
                    fgSecond: day.fgSecond?.getRawValue(),
                }))
            ),
        };
    }

    private restoreSnapshot(snapshot: ResettleSnapshot) {
        snapshot.weeks.forEach(({ week, sendToAfas, settling }) => {
            week.sendToAfas = sendToAfas;
            week.settling = settling;
        });

        snapshot.days.forEach(({ day, settlements, settling, travelTo, fg, fgSecond }) => {
            day.settlements = settlements;
            day.settling = settling;
            day.travelTo = travelTo;

            if (fg) {
                day.fg.reset(fg, { emitEvent: false });
            }

            if (fgSecond) {
                day.fgSecond.reset(fgSecond, { emitEvent: false });
            }
        });

        // Eén event aan het eind laat de periodetotalen herberekenen.
        snapshot.days[0]?.day.fg?.updateValueAndValidity();
    }

    async applyCorrections(
        differences: ResettleDifference[],
        employee: Readonly<User>,
        onProgress?: (done: number, total: number) => void
    ): Promise<Settlement[]> {
        const created: Settlement[] = [];

        const accepted = differences.filter(d => d.correctable && d.useRecalculated);

        onProgress?.(0, accepted.length);

        for (const difference of accepted) {
            // Nog niet verstuurde correcties van een eerdere ronde vervallen.
            for (const id of difference.obsoleteCorrectionIds) {
                await lastValueFrom(this.settlementService.deleteSettlements(id));
            }

            const settlement = this.buildCorrection(difference, employee);

            const result = await lastValueFrom(this.settlementService.save(settlement));

            created.push(result?.data ?? settlement);

            onProgress?.(created.length, accepted.length);
        }

        return created;
    }

    private buildCorrection(difference: ResettleDifference, employee: Readonly<User>): Settlement {
        const settlement = new Settlement();

        settlement.user_id = employee.id;
        settlement.afas_employee_id = employee.afas_employee_id;
        settlement.bookdate = formatDate(difference.date, 'yyyy-MM-dd', 'nl') as any;
        settlement.hourtype_code = difference.hourtypeCode;
        settlement.afas_project_id = difference.afasProjectId;
        settlement.minutes = difference.delta;
        settlement.amount = this.amountFor(difference.hourtypeCode, difference.delta);
        settlement.corrected = true;

        settlement.description = `${CORRECTION_PREFIX}${difference.description}`.substring(0, MAX_DESCRIPTION_LENGTH);

        return settlement;
    }

    private roundedDelta(hourtypeCode: string, minutes: number) {
        if (NON_TIME_HOURTYPES.includes(hourtypeCode)) {
            return minutes;
        }

        return Math.round(minutes / ROUNDING_MINUTES) * ROUNDING_MINUTES;
    }

    /** `CRW` telt kilometers in `minutes`, de overige uursoorten decimale uren. */
    private amountFor(hourtypeCode: string, minutes: number) {
        if (NON_TIME_HOURTYPES.includes(hourtypeCode)) {
            return `${minutes}`;
        }

        return `${Math.ceil((minutes * 100) / 60) / 100}`;
    }

    private compare(
        weeks: Week[],
        snapshot: ResettleSnapshot,
        recalculateTravel: boolean,
        settleService: Settleable
    ): ResettleDifference[] {
        const differences: ResettleDifference[] = [];

        const existingByDay = new Map(snapshot.days.map(({ day, settlements }) => [day, settlements]));

        weeks.forEach(week =>
            week.weekDays.forEach(day => {
                const existing = existingByDay.get(day) ?? [];
                const recalculated = day.settlements ?? [];

                const codes = new Set([...existing, ...recalculated].map(s => s.hourtype_code));

                // Bestaande reisuren en kosten laten staan; dagen zonder worden wel aangevuld.
                const keepTravel =
                    !recalculateTravel && existing.some(s => TRAVEL_HOURTYPE_CODES.includes(s.hourtype_code));

                codes.forEach(code => {
                    if (keepTravel && TRAVEL_HOURTYPE_CODES.includes(code)) {
                        return;
                    }

                    const difference = this.differenceFor(day, code, existing, recalculated, settleService);

                    if (difference) {
                        differences.push(difference);
                    }
                });
            })
        );

        return differences.sort(
            (a, b) => a.date.getTime() - b.date.getTime() || a.hourtypeCode.localeCompare(b.hourtypeCode)
        );
    }

    private differenceFor(
        day: WeekDay,
        hourtypeCode: string,
        existing: Settlement[],
        recalculated: Settlement[],
        settleService: Settleable
    ): ResettleDifference | null {
        const existingForCode = existing.filter(s => s.hourtype_code === hourtypeCode);

        // Alleen al verstuurde correcties tellen mee als huidige stand; de rest vervalt.
        const counting = existingForCode.filter(s => !s.corrected || !!s.afas_ptrealization_id);

        const obsolete = existingForCode.filter(s => s.corrected && !s.afas_ptrealization_id);

        const recalculatedForCode = recalculated.filter(s => s.hourtype_code === hourtypeCode);

        const current = this.sum(counting);
        const total = this.sum(recalculatedForCode);
        const delta = total - current;

        /**
         * Een verschil dat op nul uitkomt na afronding telt niet mee: dat is ruis uit opnieuw
         * berekende routeafstanden en levert geen andere boeking op.
         */
        const bookableDelta = this.roundedDelta(hourtypeCode, delta);

        if (bookableDelta === 0 && obsolete.length === 0) {
            return null;
        }

        return {
            date: day.date,
            hourtypeCode,
            hourtypeDescription:
                settleService.hourTypes?.find(h => h.code === hourtypeCode)?.description ?? hourtypeCode,
            current,
            recalculated: total,
            delta: bookableDelta,
            correctable: bookableDelta > 0,
            useRecalculated: bookableDelta > 0,
            afasProjectId: recalculatedForCode[0]?.afas_project_id ?? counting[0]?.afas_project_id,
            description: recalculatedForCode[0]?.description ?? '',
            obsoleteCorrectionIds: obsolete.map(s => s.id).filter(id => !!id),
        };
    }

    private sum(settlements: Settlement[]) {
        return settlements.reduce((total, settlement) => total + (settlement.minutes ?? 0), 0);
    }
}
